import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Schema} from 'schemas/SchemaConstants';
import {
  isTagSchema,
  isRequestSchema,
  isResultSchema,
  isRunSchema,
  isSchemaInterface,
} from 'schemas/SchemaPredicates';
import APIUtil from 'util/APIUtil';

import {LeafType, ObjectLeafTypes} from './BenchlingTypeDef';

// Keep in sync with MODEL_CLS_TO_TYPE_DEF_CLS in benchling/data_types/benchling_type_def/model_to_type_def.py
export const TYPE_DEF_TO_MODEL_TYPE: {
  [typeDef in Exclude<ObjectLeafTypes, LeafType.BENCHLING_OBJECT>]: ModelTypeConstants;
} = {
  [LeafType.BASIC_FOLDER_ITEM]: ModelTypeConstants.BASIC_FOLDER_ITEM,
  [LeafType.BATCH]: ModelTypeConstants.BATCH,
  [LeafType.BIOENTITY]: ModelTypeConstants.BIOENTITY_MIXIN,
  [LeafType.BLOB]: ModelTypeConstants.BLOB,
  [LeafType.BOX]: ModelTypeConstants.BOX,
  [LeafType.CONTAINER]: ModelTypeConstants.CONTAINER,
  [LeafType.DROPDOWN_OPTION]: ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION,
  [LeafType.USER]: ModelTypeConstants.USER,
  [LeafType.ENTRY]: ModelTypeConstants.ENTRY,
  [LeafType.MIXTURE]: ModelTypeConstants.MIXTURE,
  [LeafType.MOLECULE]: ModelTypeConstants.MOLECULE,
  [LeafType.LOCATION]: ModelTypeConstants.LOCATION,
  [LeafType.PLATE]: ModelTypeConstants.PLATE,
  [LeafType.PROCESS]: ModelTypeConstants.PROCESS,
  [LeafType.PROCESS_OUTPUT]: ModelTypeConstants.PROCESS_OUTPUT,
  [LeafType.PROCESS_TASK]: ModelTypeConstants.PROCESS_TASK,
  [LeafType.PROTEIN]: ModelTypeConstants.PROTEIN,
  [LeafType.PROTOCOL]: ModelTypeConstants.PROTOCOL,
  [LeafType.REQUEST]: ModelTypeConstants.REQUEST,
  [LeafType.RESULT]: ModelTypeConstants.ASSAY_RESULT,
  [LeafType.RUN]: ModelTypeConstants.ASSAY_RUN,
  [LeafType.SEQUENCE]: ModelTypeConstants.SEQUENCE,
  [LeafType.STORABLE]: ModelTypeConstants.STORABLE,
  [LeafType.STUDY]: ModelTypeConstants.STUDY,
  [LeafType.TEAM]: ModelTypeConstants.TEAM,
  [LeafType.TEST_ORDER]: ModelTypeConstants.TEST_ORDER,
};

export const FOLDER_ITEM_TYPE_TO_TYPE_DEF: {
  [folderItemType in TagSchemaSupportedFolderItemType]: LeafType;
} = {
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: LeafType.BASIC_FOLDER_ITEM,
  [ModelTypeConstants.MOLECULE]: LeafType.MOLECULE,
  [ModelTypeConstants.MIXTURE]: LeafType.MIXTURE,
  [ModelTypeConstants.PROTEIN]: LeafType.PROTEIN,
  [ModelTypeConstants.SEQUENCE]: LeafType.SEQUENCE,
  [ModelTypeConstants.ENTRY]: LeafType.ENTRY,
  [ModelTypeConstants.PROTOCOL]: LeafType.PROTOCOL,
};

const SCHEMA_TO_TYPE_DEF: Array<{isSchemaFn: (schema: Schema) => boolean; typeDef: LeafType}> = [
  {
    isSchemaFn: ({id}) => APIUtil.isProcessTaskSchema(id),
    typeDef: LeafType.PROCESS_TASK,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isProcessOutputSchema(id),
    typeDef: LeafType.PROCESS_OUTPUT,
  },
  {
    isSchemaFn: (schema) => isRunSchema(schema),
    typeDef: LeafType.RUN,
  },
  {
    isSchemaFn: (schema) => isResultSchema(schema),
    typeDef: LeafType.RESULT,
  },
  {
    isSchemaFn: (schema) => isRequestSchema(schema),
    typeDef: LeafType.REQUEST,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isBoxSchema(id),
    typeDef: LeafType.BOX,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isContainerSchema(id),
    typeDef: LeafType.CONTAINER,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isLocationSchema(id),
    typeDef: LeafType.LOCATION,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isPlateSchema(id),
    typeDef: LeafType.PLATE,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isBatchSchema(id),
    typeDef: LeafType.BATCH,
  },
  {
    isSchemaFn: ({id}) => APIUtil.isStudySchema(id),
    typeDef: LeafType.STUDY,
  },
];

export function getItemTypeDefForSchema(schema: Schema): LeafType {
  if (isTagSchema(schema)) {
    return FOLDER_ITEM_TYPE_TO_TYPE_DEF[schema.folderItemType];
  } else if (isSchemaInterface(schema)) {
    // keep in sync with benchling/data_types/benchling_type_def/mappings/model_to_type_def.py
    if (!schema.baseBioentityType) {
      return LeafType.BIOENTITY;
    }
    if (schema.baseBioentityType?.folderItemType) {
      return FOLDER_ITEM_TYPE_TO_TYPE_DEF[schema.baseBioentityType.folderItemType];
    }
  }

  const matchingType = SCHEMA_TO_TYPE_DEF.find(({isSchemaFn}) => isSchemaFn(schema));
  assert(matchingType != null, 'Expected to find matching type');
  return matchingType.typeDef;
}

export function getTypeDefFromSchemaOrItemType(
  schema: Schema | null,
  folderItemType: TagSchemaSupportedFolderItemType | null,
  defaultTypeDef: LeafType
): LeafType {
  if (schema != null) {
    return getItemTypeDefForSchema(schema);
  } else if (folderItemType != null) {
    return FOLDER_ITEM_TYPE_TO_TYPE_DEF[folderItemType];
  }
  return defaultTypeDef;
}
